import React, { useState, useEffect, useMemo } from 'react';
import { Card, Space, Dropdown, Modal, Tooltip } from 'antd';
import { LikeOutlined, StarOutlined, StarFilled, MoreOutlined, LoadingOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { PlayIcon, TagIcon, TrashSimpleIcon, CloudArrowDownIcon, ChatTeardropDotsIcon } from '@phosphor-icons/react';
import { formatCount } from '../utils/postList';
import { CommentBubble } from './CommentBubble';
import type { CloudPost } from '../types';
import '../../styles/PostCard.css';

/**
 * 云端帖子卡片组件 Props
 *
 * 用于广场、订阅、云剪藏簿等云端帖子的展示
 */
export interface PostCardProps {
  /** 帖子数据（CloudPost 格式） */
  post: CloudPost;
  /** 卡片点击回调 */
  onClick: () => void;
  /** 平台类型：影响图片加载和某些交互 */
  platform?: 'web' | 'electron';
  /** 是否是云端帖子（云剪藏簿/广场/订阅） */
  isCloudPost?: boolean;
  /** 是否显示博主信息 */
  showBloggerInfo?: boolean;

  // ========== 可选操作回调 ==========
  /** 切换星标回调 */
  onToggleStar?: () => void | Promise<void>;
  /** 删除帖子回调 */
  onDelete?: () => void | Promise<void>;
  /** 编辑标签回调 */
  onEditTags?: () => void;
  /** 剪藏到本地回调（Electron 云端帖子专用，已废弃，改用 onCollectToCloud） */
  onClipToLocal?: (post: CloudPost) => void;
  /** 藏为己有回调（广场他人分享的帖子） */
  onCollectToCloud?: (post: CloudPost) => void;
  /** 下载应用按钮回调（Web 广场帖子专用） */
  onDownloadApp?: () => void;
  /** 完整剪藏回调（预览帖子专用） */
  onFullClip?: () => void | Promise<void>;
  /** 帖子更新后的刷新回调 */
  onRefresh?: () => void;

  // ========== 批量选择 ==========
  /** 是否被选中 */
  isSelected?: boolean;
  /** 切换选中回调 */
  onToggleSelection?: (postId: string) => void;
}

/**
 * 云端帖子卡片组件
 *
 * 用于广场、订阅、云剪藏簿等云端帖子的展示。
 * 通过 props 控制平台差异和可选操作。
 */
export const PostCard: React.FC<PostCardProps> = ({
  post,
  onClick,
  platform = 'web',
  isCloudPost = false,
  showBloggerInfo,
  onToggleStar,
  onDelete,
  onEditTags,
  onClipToLocal,
  onCollectToCloud,
  onDownloadApp,
  onFullClip,
  onRefresh,
  isSelected = false,
  onToggleSelection,
}) => {
  const [coverImage, setCoverImage] = useState('');
  const [isHovered, setIsHovered] = useState(false);
  const [isScrapingMore, setIsScrapingMore] = useState(false);
  const [isStarLoading, setIsStarLoading] = useState(false);

  // 是否显示 hover 操作按钮
  // 云端帖子（云剪藏簿）和本地帖子都可以显示操作按钮，只要有对应回调
  const showActionButtons = onToggleStar || onEditTags || onDelete;

  // 更多菜单项
  const moreMenuItems: MenuProps['items'] = useMemo(() => {
    const items: MenuProps['items'] = [];

    if (onEditTags) {
      items.push({
        key: 'edit-tags',
        icon: <TagIcon weight="bold" size={16} />,
        label: '编辑标签',
        onClick: (e) => {
          e.domEvent.stopPropagation();
          onEditTags();
        }
      });
    }

    if (onDelete) {
      items.push({
        key: 'delete',
        icon: <TrashSimpleIcon weight="bold" size={16} />,
        label: '删除',
        danger: true,
        onClick: (e) => {
          e.domEvent.stopPropagation();
          handleDeletePost(e.domEvent as unknown as React.MouseEvent);
        }
      });
    }

    return items;
  }, [onEditTags, onDelete]);

  // 加载封面图片：优先使用 R2 备份图片
  useEffect(() => {
    const loadCoverImage = async () => {
      try {
        // 优先使用 R2 备份图片
        if (post.backupImages) {
          const backups = typeof post.backupImages === 'string'
            ? JSON.parse(post.backupImages)
            : post.backupImages;
          if (Array.isArray(backups) && backups.length > 0) {
            const firstBackup = backups[0];
            const url = typeof firstBackup === 'string' ? firstBackup : firstBackup.url;
            if (url) {
              setCoverImage(url);
              return;
            }
          }
        }
        // 使用原始网络图片
        const images = typeof post.coverImages === 'string'
          ? JSON.parse(post.coverImages || '[]')
          : (Array.isArray(post.coverImages) ? post.coverImages : []);
        setCoverImage(images[0] || '');
      } catch (e) {
        setCoverImage('');
      }
    };

    loadCoverImage();
  }, [post.backupImages, post.coverImages]);

  // 卡片点击处理：统一打开详情弹窗
  const handleCardClick = () => {
    onClick();
  };

  // 标题点击：跳转到小红书帖子页
  const handleTitleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (platform === 'web') {
      window.open(post.postUrl, '_blank');
    }
    // Electron 平台需要通过 electronAPI 打开，这里不处理
  };

  // 切换星标
  const handleToggleStar = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onToggleStar || isStarLoading) return;

    setIsStarLoading(true);
    try {
      await onToggleStar();
    } finally {
      setIsStarLoading(false);
    }
  };

  // 删除帖子
  const handleDeletePost = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onDelete) return;

    Modal.confirm({
      title: '确认删除',
      content: `确定要删除帖子 "${post.title}" 吗？`,
      okText: '确认删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        await onDelete();
      }
    });
  };

  // 剪藏到本地
  const handleClipToLocal = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClipToLocal?.(post);
  };

  // 藏为己有（功能开发中）
  const handleCollectToCloud = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCollectToCloud?.(post);
  };

  // 下载应用按钮（Web 广场帖子）
  const handleDownloadApp = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDownloadApp?.();
  };

  // 完整剪藏（预览帖子）
  const handleFullClip = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onFullClip || isScrapingMore) return;

    setIsScrapingMore(true);
    try {
      await onFullClip();
      onRefresh?.();
    } catch (error) {
      console.error('完整剪藏失败:', error);
    } finally {
      setIsScrapingMore(false);
    }
  };

  // 点击选中/取消选中
  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleSelection?.(post.id);
  };

  return (
    <div className={`post-card-container ${isSelected ? 'post-card-selected' : ''}`}>
      <div className="post-card-bg" />

      {/* 选中状态的可点击勾选框 */}
      {isSelected && onToggleSelection && (
        <button
          className="selection-checkbox"
          onClick={handleCheckboxClick}
          title="取消选中"
        />
      )}

      <Card
        hoverable
        className="post-card"
        cover={
          <div
            className="card-cover-wrapper"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <div className="card-cover">
              {coverImage ? (
                <img
                  alt={post.title}
                  src={coverImage}
                />
              ) : (
                <div style={{
                  aspectRatio: '3/4',
                  background: 'var(--ant-color-fill-tertiary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--ant-color-text-tertiary)'
                }}>
                  暂无图片
                </div>
              )}

              {/* 视频标识 */}
              {post.isVideo && (
                <div
                  className="video-badge"
                  style={{
                    left: post.isPinned ? '52px' : '8px',
                    ...(isCloudPost ? {
                      width: 'auto',
                      maxWidth: 'calc(100% - 16px)',
                      paddingLeft: '8px',
                      paddingRight: '8px',
                    } : {})
                  }}
                >
                  <PlayIcon size={12} weight="fill" style={{ marginLeft: '0px', marginRight: '4px' }} />
                  {isCloudPost ? '视频帖' : '视频'}
                </div>
              )}

              {/* 置顶标识 */}
              {post.isPinned && (
                <div className="pinned-badge">
                  置顶
                </div>
              )}

              {/* 预览状态：显示完整剪藏按钮（Electron 或 Web 云端帖子） */}
              {post.isPreview && onFullClip && (
                <button
                  className={`scrape-more-btn ${isHovered || isScrapingMore ? 'scrape-more-btn-visible' : ''}`}
                  onClick={handleFullClip}
                  disabled={isScrapingMore}
                  style={{
                    opacity: isScrapingMore ? 0.6 : 1,
                    cursor: isScrapingMore ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isScrapingMore ? (
                    <>
                      <LoadingOutlined style={{ fontSize: 16 }} spin />
                      <span>抓取中...</span>
                    </>
                  ) : (
                    <>
                      <CloudArrowDownIcon weight="bold" style={{ fontSize: 16 }} />
                      <span>完整剪藏</span>
                    </>
                  )}
                </button>
              )}

              {/* 右上角操作按钮（加星、更多）：非云端帖子 hover 时显示 */}
              {showActionButtons && (
                <div className={`card-action-buttons ${isHovered && !isScrapingMore ? 'card-action-buttons-visible' : ''}`}>
                  {onToggleStar && (
                    <button
                      className={`card-action-btn ${post.isStarred ? 'starred' : ''}`}
                      onClick={handleToggleStar}
                      title={post.isStarred ? '取消星标' : '加星标'}
                      disabled={isStarLoading}
                    >
                      {isStarLoading ? (
                        <LoadingOutlined style={{ fontSize: 16 }} spin />
                      ) : post.isStarred ? (
                        <StarFilled style={{ fontSize: 16 }} />
                      ) : (
                        <StarOutlined style={{ fontSize: 16 }} />
                      )}
                    </button>
                  )}
                  {moreMenuItems.length > 0 && (
                    <Dropdown
                      menu={{ items: moreMenuItems }}
                      trigger={['click']}
                      placement="bottomRight"
                    >
                      <button
                        className="card-action-btn"
                        onClick={(e) => e.stopPropagation()}
                        title="更多"
                      >
                        <MoreOutlined style={{ fontSize: 16 }} />
                      </button>
                    </Dropdown>
                  )}
                </div>
              )}

              {/* 右下角"剪藏到本地"按钮：Electron 云端帖子专用 */}
              {isCloudPost && platform === 'electron' && onClipToLocal && (
                <button
                  className={`card-action-btn-with-text ${isHovered && !isScrapingMore ? 'card-action-buttons-visible' : ''}`}
                  onClick={handleClipToLocal}
                  title="剪藏到本地"
                >
                  <CloudArrowDownIcon weight="bold" style={{ fontSize: 16 }} />
                  <span>剪藏到本地</span>
                </button>
              )}

              {/* 右上角评论按钮 - 非 owner 视角的云端帖子 */}
              {isCloudPost && onCollectToCloud && (
                <div className={`card-action-buttons ${isHovered && !isScrapingMore ? 'card-action-buttons-visible' : ''}`}>
                  <button
                    className="card-action-btn"
                    onClick={onClick}
                    title="查看评论"
                  >
                    <ChatTeardropDotsIcon size={16} weight="bold" />
                  </button>
                </div>
              )}

              {/* 右下角"藏为己有"按钮 - 保存云端他人分享的帖子 */}
              {isCloudPost && onCollectToCloud && (
                <Tooltip title="功能正在开发中..." placement="top">
                  <button
                    className={`card-action-btn-with-text ${isHovered && !isScrapingMore ? 'card-action-buttons-visible' : ''}`}
                    onClick={handleCollectToCloud}
                    style={{ cursor: 'not-allowed' }}
                  >
                    <CloudArrowDownIcon weight="bold" style={{ fontSize: 16 }} />
                    <span>藏为己有</span>
                  </button>
                </Tooltip>
              )}

              {/* 右下角"下载应用以剪藏"按钮：Web 广场帖子专用 */}
              {!isCloudPost && platform === 'web' && onDownloadApp && (
                <button
                  className={`card-action-btn-with-text ${isHovered ? 'card-action-buttons-visible' : ''}`}
                  onClick={handleDownloadApp}
                  title="下载应用以剪藏"
                >
                  <CloudArrowDownIcon weight="bold" style={{ fontSize: 16 }} />
                  <span>下载应用以剪藏</span>
                </button>
              )}
            </div>

            {/* 未读角标 */}
            {post.isNew && (
              <div className="unread-badge" />
            )}

            {/* 评论气泡 - 云端帖子有最新评论时显示 */}
            {isCloudPost && post.latestComment && (
              <CommentBubble
                content={post.latestComment.content}
                avatarUrl={post.latestComment.user.avatarUrl}
                username={post.latestComment.user.username}
              />
            )}
          </div>
        }
        onClick={handleCardClick}
      >
        <div className="post-card-meta-custom">
          <Tooltip
            title={post.postUrl}
            placement="topLeft"
            mouseEnterDelay={0.5}
          >
            <div
              className="post-card-title-custom"
              onClick={handleTitleClick}
            >
              {post.title}
            </div>
          </Tooltip>

          {showBloggerInfo && post.blogger && (
            <div className="post-card-blogger-info">
              {post.blogger.avatarUrl && (
                <img
                  src={post.blogger.avatarUrl}
                  alt={post.blogger.nickname || ''}
                  className="blogger-avatar-small"
                />
              )}
              <span className="blogger-nickname-small">
                {post.blogger.nickname || '未知博主'}
              </span>
            </div>
          )}

          <div className="post-card-stats">
            <Space size="large">
              <span>
                <LikeOutlined /> {formatCount(post.likedCount)}
              </span>
              {!post.isPreview && !!post.collectCount && (
                <span>
                  <StarOutlined /> {formatCount(post.collectCount)}
                </span>
              )}
            </Space>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default PostCard;
